/*
  Фазу и её признак «сессии уже нет» берём из `components/RoomChrome` – это чистая модель
  оболочки комнаты, просто лежащая рядом с поверхностями, которые её и завели. Своё второе
  определение фазы здесь означало бы, что хост и поверхность могут разойтись во мнении о том,
  закончилась ли сессия, – а хост как раз решает, показывать ли её вообще.
*/
import { isClosedPhase, resolveRoomPhase, type SessionRoomPhase } from '../components/RoomChrome';
import type { CurrentSessionCandidate } from './currentSession';

/**
 * Сколько плашка прощания держится на экране, пока её не закрыли руками.
 *
 * Минута: человек возвращается к экрану после матча, и «Сессия закончилась – чат остаётся» это
 * единственное место, где ему скажут, куда делась переписка.
 *
 * Один срок действует и для завершения, и для отмены: обе плашки нужны лишь как короткая дверь
 * в сохранившийся чат.
 */
export const ENDED_FAREWELL_MS = 60_000;

/**
 * Чем закончилась сессия, которую комната только что показывала, – если она вообще закончилась.
 *
 * Правило показа (`pickCurrentSession`) отдаёт только живые и вот-вот начинающиеся сессии, и это
 * верно: завершённой сессии в очереди на показ не место. Но исчезнуть с экрана без единого слова
 * она не имеет права – человек только что писал в этот чат. Поэтому прощание строится не на
 * отборе, а на разнице: сессия, которую комната держала, пропала из отбора – почему?
 *
 * Отвечает сам список `mySessions`, а не часы:
 *  – строка на месте и завершена (COMPLETED) или её время вышло – сессия доиграла, и об этом
 *    надо сказать;
 *  – строка отменена – показываем отдельное короткое прощание и вход в оставшийся чат;
 *  – строки нет вовсе – меня в этой сессии больше нет: вышел сам, исключили, отменили, заявку
 *    удалили. Тоже молча. «Сессия закончилась» тому, кто сам нажал «Выйти», – ложь, а отличить
 *    выход от исключения по этому списку нечем: пропадает строка одинаково.
 *
 * Завершение и отмену различает `resolveRoomPhase`; обе сохраняют чат.
 */
export const resolveFarewellPhase = (
  previousSessionId: string,
  sessions: readonly CurrentSessionCandidate[],
  now: number
): SessionRoomPhase | null => {
  const previous = sessions.find((candidate) => candidate.sessionId === previousSessionId);

  if (!previous) {
    return null;
  }

  const phase = resolveRoomPhase({
    status: previous.status,
    startedAt: previous.startedAt,
    expiredAt: previous.expiredAt,
    now,
  });

  return isClosedPhase(phase) ? phase : null;
};

type PlacementInput = {
  /** Гостю комнаты нет: ни сессий, ни голоса у него не бывает. */
  isAuth: boolean;
  /**
   * Страница со своей шапкой – сегодня это один лендинг Discord-бота.
   *
   * Правило её больше не гасит (см. `isRoomAllowedHere`), но поле остаётся в контракте: его
   * передаёт хост, и убрать оттуда аргумент – отдельная правка в чужом файле.
   */
  /** Открыт тред ровно этой сессии: всё содержимое комнаты уже на странице. */
  isOwnSessionThread: boolean;
  /** Телефон – ниже `MOBILE_BREAKPOINT_PX`. */
  isMobile: boolean;
  /** Личный звонок в живых фазах: `incoming` / `outgoing` / `active`. */
  callActive: boolean;
  /** Адрес страницы – ради `/banned` и `/unsubscribe`. */
  pathname: string;
};

/**
 * Маршруты, на которых комнаты нет никогда.
 *
 * Оба лежат вне `_layout`, то есть хост там и не смонтирован, – проверка стоит второй страховкой:
 * это экраны «вас заблокировали» и «отписаться от писем», и плавающая коробка с микрофоном поверх
 * любого из них выглядит как издевательство. Перенесут их когда-нибудь внутрь лейаута – правило
 * переживёт перенос.
 */
const ROOMLESS_PATHS = ['/banned', '/unsubscribe'];

/**
 * Можно ли показывать комнату на этом экране.
 *
 * Про звонок 1:1 правило разное по платформам, и это не непоследовательность: на десктопе окно
 * звонка стоит в своём углу, комната в своём, и ярус голоса честно говорит «микрофон занят
 * звонком». На телефоне низ экрана целиком отдан звонку, и полоса комнаты легла бы на кнопку
 * «ответить».
 *
 * Страница со своей шапкой (`rendersOwnHeader`) комнату НЕ гасит – и это сознательная отмена
 * прежнего правила. Оно писалось, когда комната была индикатором «сессия идёт»: индикатором
 * на лендинге действительно можно пренебречь. Теперь комната несёт мой микрофон, и на
 * `/discord-lfg-bot` человек в голосе оставался без единственной кнопки, которой его
 * выключают, – а зайти туда из сессии есть зачем: привязку Discord предлагает сам диалог
 * настроек сессии.
 *
 * Что комната там перекрывает, проверено по коду лендинга, а не на глаз: на десктопе у него
 * внизу не закреплено ничего (`BotHeader` – `position: sticky` сверху), на телефоне – одна
 * кнопка `StickyMobileInvite` («Add to Discord», `bottom: 0`, появляется после 640px
 * прокрутки). Полоса встаёт над ней и закрывает, но это ЧЕТВЁРТЫЙ экземпляр той же кнопки:
 * она же стоит в липкой шапке, которая на телефоне видна всегда, и трижды в тексте страницы.
 * Цена – закрытый дубль призыва для человека, который по определению уже зарегистрирован
 * (комнаты без входа не бывает вовсе). Цена обратного решения – отобранный микрофон.
 */
export const isRoomAllowedHere = ({
  isAuth,
  isOwnSessionThread,
  isMobile,
  callActive,
  pathname,
}: PlacementInput): boolean => {
  if (!isAuth || isOwnSessionThread) {
    return false;
  }

  if (ROOMLESS_PATHS.some((path) => pathname === path || pathname.endsWith(path))) {
    return false;
  }

  return !(isMobile && callActive);
};
